import { Table, TableFooter, TableRow, TableCell } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useIntuiTable } from './useIntuiTable'
import { IntuiTableHeader } from './IntuiTableHeader'
import { IntuiTableBody } from './IntuiTableBody'
import { IntuiTablePagination } from './IntuiTablePagination'
import type { IntuiTableProps } from './types'

function IntuiTable<T>(props: IntuiTableProps<T>) {
  const {
    getRowId,
    loading,
    emptyState,
    sortable,
    selectable,
    selectionMode = 'multi',
    isRowSelectable,
    onRowClick,
    onRowDoubleClick,
    onRowContextMenu,
    isRowDisabled,
    getRowClassName,
    striped,
    bordered,
    compact,
    stickyHeader,
    maxHeight,
    className,
    rowActions,
    expandable,
    getExpandedContent,
    footer,
    caption,
  } = props

  const {
    sortState,
    handleSort,
    selectedIds,
    handleSelectRow,
    handleSelectAll,
    isAllSelected,
    isIndeterminate,
    paginationConfig,
    page,
    pageSize,
    paginatedData,
    totalPages,
    totalDataRows,
    handlePageChange,
    handlePageSizeChange,
    expandedIds,
    handleToggleExpand,
    visibleColumns,
    totalColumnCount,
  } = useIntuiTable(props)

  return (
    <div
      className={cn(
        'w-full bg-background',
        className
      )}
    >
      <div
        className={cn(
          'overflow-auto',
          stickyHeader && 'relative'
        )}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <Table>
          <IntuiTableHeader
            columns={visibleColumns}
            compact={compact}
            bordered={bordered}
            stickyHeader={stickyHeader}
            sortable={sortable}
            sortState={sortState}
            onSort={handleSort}
            selectable={selectable}
            selectionMode={selectionMode}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            onSelectAll={handleSelectAll}
            expandable={expandable}
            hasRowActions={!!rowActions?.length}
          />
          <IntuiTableBody
            data={paginatedData}
            columns={visibleColumns}
            getRowId={getRowId}
            loading={loading}
            emptyState={emptyState}
            compact={compact}
            striped={striped}
            bordered={bordered}
            selectable={selectable}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            isRowSelectable={isRowSelectable}
            onSelectRow={handleSelectRow}
            expandable={expandable}
            expandedIds={expandedIds}
            onToggleExpand={handleToggleExpand}
            getExpandedContent={getExpandedContent}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            onRowContextMenu={onRowContextMenu}
            isRowDisabled={isRowDisabled}
            getRowClassName={getRowClassName}
            rowActions={rowActions}
            totalColumnCount={totalColumnCount}
          />
          {/* Figma: Footer row with bg-muted/50 background, font-medium */}
          {footer && (
            <TableFooter className="bg-muted/50 border-t border-border">
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={totalColumnCount}
                  className="p-2 text-sm font-medium text-foreground"
                >
                  {footer}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>

      {/* Figma: Caption below table, centered, text-muted-foreground, pt-4 */}
      {caption && (
        <div className="flex items-center justify-center pt-4">
          <p className="text-sm font-normal text-muted-foreground text-center">
            {caption}
          </p>
        </div>
      )}

      {paginationConfig && (
        <IntuiTablePagination
          config={paginationConfig}
          currentPage={page}
          totalPages={totalPages}
          totalRows={totalDataRows}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  )
}

export { IntuiTable }
